/**
 * Block editor helpers (#19): block list manipulation (pure) and the conversion between the stored
 * text runs and what the contentEditable shows. The backend (services/email_blocks.py) is the real
 * validator; these helpers only make sure the editor can't produce anything it would reject.
 */
import { EmailBlock, EmailColourSlot, EmailPalette, EmailRun } from '../types/api';
import { MergeFieldInfo } from './emailEditor';

export const MAX_BLOCKS = 30;
export const MAX_BUTTONS = 3;
export const MAX_TEXT_CHARS = 2000;

export const DEFAULT_PALETTE: EmailPalette = { primary: '#253473', secondary: '#ff0e0e', accent: '#232751', neutral: '#666666' };
export const SLOT_LABELS: Record<EmailColourSlot, string> = {
  primary: 'Principale', secondary: 'Secondaire', accent: 'Accent', neutral: 'Neutre',
};

// The app-wide palette (#22) is a singleton: the editor reads the live hex values through COLOUR_SLOTS.
let livePalette: EmailPalette = { ...DEFAULT_PALETTE };
export const setEmailPalette = (p: EmailPalette) => { livePalette = { ...DEFAULT_PALETTE, ...p }; };

export const COLOUR_SLOTS: { slot: EmailColourSlot; hex: string; label: string }[] = (
  Object.keys(DEFAULT_PALETTE) as EmailColourSlot[]
).map((slot) => ({ slot, label: SLOT_LABELS[slot], get hex() { return livePalette[slot]; } }));

export type BlockType = EmailBlock['type'];

export const BLOCK_LABELS: Record<BlockType, string> = {
  text: 'Texte',
  line: 'Ligne',
  button: 'Bouton',
  spacing: 'Espacement',
  qr: "QR code d'accès",
};

// Short hint under a block type in the "Ajouter un bloc" menu (#32: the vertical spacing block must be easy to find).
export const BLOCK_HINTS: Partial<Record<BlockType, string>> = {
  spacing: 'Ajoute un espace vertical',
};

/**
 * Text sizes (#30): three fixed sizes, same px as the renderer (11 / 14 / 18). #31: an explicit pixel
 * line-height per size (~1.4-1.5x), same values as TEXT_LINE_HEIGHT_PX in the backend renderer, so the
 * editor, the preview and the sent email match.
 */
export const TEXT_SIZES: { value: 'small' | 'normal' | 'large'; label: string; px: number; lineHeightPx: number }[] = [
  { value: 'small', label: 'Petit', px: 11, lineHeightPx: 16 },
  { value: 'normal', label: 'Normal', px: 14, lineHeightPx: 21 },
  { value: 'large', label: 'Grand', px: 18, lineHeightPx: 26 },
];

export const SIZE_TOOLTIP =
  "La taille s'applique à tout le bloc. Pour afficher un champ dans une autre taille, placez-le dans son propre bloc texte.";

/** Line block (#31): named thicknesses, same px as the renderer (1 / 2 / 4). */
export type LineThickness = 'thin' | 'medium' | 'thick';
export const LINE_THICKNESSES: { value: LineThickness; label: string; px: number }[] = [
  { value: 'thin', label: 'Fin', px: 1 },
  { value: 'medium', label: 'Moyen', px: 2 },
  { value: 'thick', label: 'Épais', px: 4 },
];

export const newBlock = (type: BlockType): EmailBlock => {
  switch (type) {
    case 'text':
      return { type: 'text', align: 'left', size: 'normal', paragraphs: [[{ text: '' }]] };
    case 'line':
      return { type: 'line', colour: 'primary', thickness: 'thin' };
    case 'button':
      return { type: 'button', label: 'Nouveau bouton', colour: 'secondary', target: 'rsvp_form', url: null };
    case 'spacing':
      return { type: 'spacing', size: 'medium' };
    default:
      return { type: 'qr' };
  }
};

export const countButtons = (blocks: EmailBlock[]) => blocks.filter((b) => b.type === 'button').length;

/** Can a block of this type be added or duplicated? Returns a reason when not. */
export function addLimit(blocks: EmailBlock[], type: BlockType): string | null {
  if (blocks.length >= MAX_BLOCKS) return `Maximum ${MAX_BLOCKS} blocs`;
  if (type === 'button' && countButtons(blocks) >= MAX_BUTTONS) return `Maximum ${MAX_BUTTONS} boutons`;
  return null;
}

export const moveBlock = (blocks: EmailBlock[], from: number, to: number): EmailBlock[] => {
  if (from === to || from < 0 || to < 0 || from >= blocks.length || to >= blocks.length) return blocks;
  const next = blocks.slice();
  const [b] = next.splice(from, 1);
  next.splice(to, 0, b);
  return next;
};

export const duplicateBlock = (blocks: EmailBlock[], i: number): EmailBlock[] => {
  if (i < 0 || i >= blocks.length || addLimit(blocks, blocks[i].type)) return blocks;
  const next = blocks.slice();
  next.splice(i + 1, 0, JSON.parse(JSON.stringify(blocks[i])));
  return next;
};

export const removeBlock = (blocks: EmailBlock[], i: number): EmailBlock[] => blocks.filter((_, j) => j !== i);

export const insertBlock = (blocks: EmailBlock[], at: number, block: EmailBlock): EmailBlock[] => {
  if (addLimit(blocks, block.type)) return blocks;
  const next = blocks.slice();
  next.splice(Math.max(0, Math.min(at, blocks.length)), 0, block);
  return next;
};

export const textLength = (paragraphs: EmailRun[][]) =>
  paragraphs.reduce((n, p) => n + p.reduce((m, r) => m + (r.text || '').length, 0), 0);

// --- runs <-> editor DOM ---

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const hexOf = (slot?: string) => COLOUR_SLOTS.find((c) => c.slot === slot)?.hex;

/** Formatting a chip can carry (#31): kept as attributes on the chip itself, so it never depends on how the browser wraps a non-editable element. */
export interface ChipFmt { bold?: boolean; italic?: boolean; colour?: EmailColourSlot }

const chipStyle = (fmt: ChipFmt) =>
  'background:#E2E8F0;border-radius:4px;padding:0 6px;margin:0 1px;font-size:0.9em;white-space:nowrap;user-select:all;' +
  (fmt.bold ? 'font-weight:bold;' : '') + (fmt.italic ? 'font-style:italic;' : '') +
  (fmt.colour && hexOf(fmt.colour) ? 'color:' + hexOf(fmt.colour) + ';' : '');

const chip = (key: string, label: string, fmt: ChipFmt = {}) =>
  '<span data-merge-field="' + esc(key) + '" contenteditable="false"' +
  (fmt.bold ? ' data-bold="1"' : '') + (fmt.italic ? ' data-italic="1"' : '') + (fmt.colour ? ' data-colour="' + fmt.colour + '"' : '') +
  ' style="' + chipStyle(fmt) + '">' + esc(label) + '</span>';

export function chipFmtOf(el: Element): ChipFmt {
  const fmt: ChipFmt = {};
  if (el.hasAttribute('data-bold')) fmt.bold = true;
  if (el.hasAttribute('data-italic')) fmt.italic = true;
  const c = el.getAttribute('data-colour') as EmailColourSlot | null;
  if (c && COLOUR_SLOTS.some((x) => x.slot === c)) fmt.colour = c;
  return fmt;
}

/** Set or clear one formatting option on chips (used by the toolbar for a selection that contains chips). */
export function applyChipFormat(chips: HTMLElement[], kind: 'bold' | 'italic' | 'colour', value: boolean | EmailColourSlot | null): void {
  chips.forEach((el) => {
    const attr = 'data-' + kind;
    if (value === null || value === false) el.removeAttribute(attr);
    else el.setAttribute(attr, kind === 'colour' ? String(value) : '1');
    el.setAttribute('style', chipStyle(chipFmtOf(el)));
  });
}

/** The merge-field chips a selection range touches (a selection may mix text and chips). */
export const chipsInRange = (root: HTMLElement, range: Range): HTMLElement[] =>
  (Array.from(root.querySelectorAll('[data-merge-field]')) as HTMLElement[]).filter((c) => range.intersectsNode(c));

function runHtml(run: EmailRun, fields: MergeFieldInfo[]): string {
  if (run.field) {
    const label = fields.find((f) => f.key === run.field)?.label;
    return chip(run.field, label || `champ inconnu: ${run.field}`, { bold: run.bold, italic: run.italic, colour: run.colour });
  }
  let h = esc(run.text || '').replace(/\n/g, '<br>');
  if (!h) return '';
  const colour = hexOf(run.colour);
  if (colour) h = `<span data-colour="${run.colour}" style="color:${colour}">${h}</span>`;
  if (run.italic) h = `<i>${h}</i>`;
  if (run.bold) h = `<b>${h}</b>`;
  if (run.link) h = `<a href="${esc(run.link)}">${h}</a>`;
  return h;
}

export function toEditorHtml(paragraphs: EmailRun[][], fields: MergeFieldInfo[]): string {
  // A paragraph that ends with a line break gets one extra <br>: browsers don't show an empty last line
  // without it, and fromEditorHtml drops it again (#27), so the round trip is stable.
  const endsWithBreak = (p: EmailRun[]) => { const l = p[p.length - 1]; return !!l && !l.field && (l.text || '').endsWith('\n'); };
  return paragraphs
    .map((p) => `<div>${p.map((r) => runHtml(r, fields)).join('') || '<br>'}${endsWithBreak(p) ? '<br>' : ''}</div>`)
    .join('');
}

const SAFE_LINK = /^(https:\/\/|mailto:)\S+$/i;

function slotFromColour(value: string | null | undefined): EmailColourSlot | undefined {
  if (!value) return undefined;
  const v = value.trim().toLowerCase();
  let hex = v;
  const m = v.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/);
  if (m) hex = '#' + [m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('');
  return COLOUR_SLOTS.find((c) => c.hex === hex)?.slot;
}

interface Fmt { bold?: boolean; italic?: boolean; colour?: EmailColourSlot; link?: string }

const BLOCK_TAGS = ['DIV', 'P'];

/** Editor DOM -> paragraphs of runs. Only the allowed formatting survives; everything else is text. */
export function fromEditorHtml(editorHtml: string, _allowedFields?: MergeFieldInfo[]): EmailRun[][] {
  const root = document.createElement('div');
  root.innerHTML = editorHtml || '';
  const paragraphs: EmailRun[][] = [];
  let current: EmailRun[] = [];
  const flush = () => { paragraphs.push(current); current = []; };
  const push = (text: string, fmt: Fmt) => {
    if (!text) return;
    const run: EmailRun = { text };
    if (fmt.bold) run.bold = true;
    if (fmt.italic) run.italic = true;
    if (fmt.colour) run.colour = fmt.colour;
    if (fmt.link) run.link = fmt.link;
    const last = current[current.length - 1];
    if (last && !last.field && last.bold === run.bold && last.italic === run.italic && last.colour === run.colour && last.link === run.link) {
      last.text += text; // merge neighbouring runs with the same formatting
    } else current.push(run);
  };
  const walk = (node: Node, fmt: Fmt) => {
    if (node.nodeType === Node.TEXT_NODE) return push((node.textContent || '').replace(/\u00a0/g, ' '), fmt);
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const field = el.getAttribute('data-merge-field');
    if (field) {
      // unknown keys are kept (shown as a chip): the backend validator decides, so a round trip never drops them
      // #31: the run keeps its formatting: the chip's own attributes plus any <b>/<i>/colour wrapper around it
      const own = chipFmtOf(el);
      const run: EmailRun = { text: '', field };
      if (fmt.bold || own.bold) run.bold = true;
      if (fmt.italic || own.italic) run.italic = true;
      const colour = own.colour || fmt.colour;
      if (colour) run.colour = colour;
      if (fmt.link) run.link = fmt.link;
      current.push(run);
      return;
    }
    const tag = el.tagName;
    if (tag === 'BR') return push('\n', fmt);
    let next = fmt;
    if (tag === 'B' || tag === 'STRONG') next = { ...fmt, bold: true };
    else if (tag === 'I' || tag === 'EM') next = { ...fmt, italic: true };
    else if (tag === 'A') {
      const href = el.getAttribute('href') || '';
      if (SAFE_LINK.test(href) && href.length <= 500) next = { ...fmt, link: href };
    } else if (tag === 'FONT' || tag === 'SPAN') {
      const slot = slotFromColour(el.getAttribute('data-colour') ? hexOf(el.getAttribute('data-colour') as string) : el.getAttribute('color') || (el as HTMLElement).style.color);
      const raw = el.getAttribute('data-colour') ? hexOf(el.getAttribute('data-colour') as string) : el.getAttribute('color') || (el as HTMLElement).style.color;
      if (slot) next = { ...fmt, colour: slot };
      else if (raw) next = { ...fmt, colour: undefined }; // a non-palette colour (e.g. the default) clears the inherited one
    }
    if (BLOCK_TAGS.includes(tag)) {
      if (current.length) flush();
      Array.from(el.childNodes).forEach((c) => walk(c, next));
      flush();
      return;
    }
    Array.from(el.childNodes).forEach((c) => walk(c, next));
  };
  Array.from(root.childNodes).forEach((c) => walk(c, {}));
  if (current.length) flush();
  // a lone trailing <br> in an otherwise empty line is the browser's placeholder, not a typed break
  const lines = paragraphs.map((p) => {
    const last = p[p.length - 1];
    if (p.length === 1 && last.text === '\n') return [];
    return p;
  });
  return mergeLines(lines);
}

const sameFmt = (a: EmailRun, b: EmailRun) =>
  !a.field && !b.field && a.bold === b.bold && a.italic === b.italic && a.colour === b.colour && a.link === b.link;

/**
 * #27: a text block is ONE paragraph of runs. Every line the editor shows (a stray <div>/<p> from a
 * browser Enter, or pasted markup) becomes a line break (\n) inside that paragraph. Returns [] when empty.
 */
export function mergeLines(lines: EmailRun[][]): EmailRun[][] {
  const merged: EmailRun[] = [];
  const add = (run: EmailRun) => {
    const last = merged[merged.length - 1];
    if (last && sameFmt(last, run)) last.text += run.text;
    else merged.push({ ...run });
  };
  lines.forEach((line, i) => {
    if (i) add({ text: '\n' });
    line.forEach(add);
  });
  // The browser's placeholder <br> after a trailing line break is not a typed break: drop exactly one.
  const last = merged[merged.length - 1];
  if (last && !last.field && last.text.endsWith('\n\n')) last.text = last.text.slice(0, -1);
  return merged.length ? [merged] : [];
}

